import { useMemo } from 'react';
import type { IApiErrorPlain, IRecordListModel } from '@platform/core/types';
import { useListParams } from '@platform/engine/list/useListParams';
import { findModule } from '@platform/modularity/moduleRegistry';
import { useGetCatalogQuery } from '@platform/state/api';
import { baCoverageOf } from './baCoverageOf';
import { useBaCoverageStatsQuery } from './baCoverageApi';
import { baRowsOf } from './baRowsOf';
import type { IBaCoverageState } from './IBaCoverageState';

const optionsOf = (model: IRecordListModel, key: string): string[] => (model.rt.fields.find((f) => f.key === key)?.options ?? []).map((o) => o.value);

/**
 * Покриття аналізом для сторінки з моделлю списку записів аналізу: статистика API з фільтрами сторінки (пошук, швидкий фільтр, теги,
 * розширені фільтри), рядки — системні й таблиці встановлених модулів простору з базовими назвами пакетів (MOD_BASE_NAME прототипу).
 */
export function useBaCoverage(model: IRecordListModel): IBaCoverageState {
  const { ws, rt } = model;
  const params = useListParams(model);
  const kinds = useMemo(() => optionsOf(model, 'kind'), [model]);
  const stakeholders = useMemo(() => optionsOf(model, 'stakeholder'), [model]);
  const { data: stats, error } = useBaCoverageStatsQuery({ ws, rt: rt.key, params, kinds }, { skip: !kinds.length });
  const { data: catalog } = useGetCatalogQuery({ workspaceId: ws });
  const coverage = useMemo(() => {
    if (!stats || !catalog) return undefined;
    const rows = baRowsOf(catalog, (m, type) => {
      const manifest = findModule(m.key);
      return (type.main ? manifest?.name.en : manifest?.entities.find((e) => e.key === type.key)?.name.en) ?? type.name.en ?? type.key;
    });
    return baCoverageOf(rows, stats, stakeholders);
  }, [stats, catalog, stakeholders]);
  return { coverage, kinds, error: error as IApiErrorPlain | undefined };
}
